import { apiClient } from '../../../services/api/base';
import { ApiResponse } from '../../../services/api/types';
import {
	LoginRequest,
	LoginResponse,
	UserProfile,
	RefreshTokenRequest,
	RefreshTokenResponse,
} from '../types/auth.types';

// Auth API endpoints
const AUTH_ENDPOINTS = {
	LOGIN: '/api/auth/login',
	REFRESH: '/api/auth/refresh',
	LOGOUT: '/api/auth/logout',
	PROFILE: '/api/auth/profile',
} as const;

// Auth service class with clean methods
class AuthService {
	/**
	 * Login with username and password
	 */
	async login(credentials: LoginRequest): Promise<LoginResponse> {
		try {

			const response = await apiClient.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, credentials);

			if (!response || !response.data) {
				console.error('authService.login: Invalid response structure:', response);
				throw new Error('Invalid response from authentication server');
			}

			if (!response.data.accessToken) {
				console.error(
					'authService.login: Missing access token in response:',
					response.data,
				);
				throw new Error('Authentication response missing access token');
			}

			return response.data;
		} catch (error: any) {
			console.error('authService.login: Login failed:', error);

			// Extract error message from backend response
			const backendError = error.response?.data;
			
			// Handle validation errors (400) with field-specific messages
			if (error.response?.status === 400 && backendError?.errors) {
				// Get the first error message from the errors object
				const firstErrorField = Object.keys(backendError.errors)[0];
				const firstErrorMessage = backendError.errors[firstErrorField];
				throw new Error(firstErrorMessage || backendError.error || 'Validation failed');
			}
			
			// Handle other errors with backend message
			if (backendError?.error) {
				throw new Error(backendError.error);
			}

			// Fallback to generic error messages
			if (error.response?.status === 401) {
				throw new Error('Invalid credentials. Please check your username and password.');
			} else if (error.response?.status === 404) {
				throw new Error('User not found in the system.');
			} else if (error.response?.status >= 500) {
				throw new Error('Server error. Please try again later.');
			} else if (error.code === 'NETWORK_ERROR') {
				throw new Error('Network error. Please check your connection.');
			}

			throw error;
		}
	}

	/**
	 * Refresh access token using refresh token
	 */
	async refresh(refreshToken: string): Promise<ApiResponse<RefreshTokenResponse>> {
		try {
			console.log('authService.refresh: Attempting token refresh');

			const request: RefreshTokenRequest = { refreshToken };
			const response = await apiClient.post<RefreshTokenResponse>(
				AUTH_ENDPOINTS.REFRESH,
				request,
			);

			console.log('authService.refresh: Token refresh successful');
			return response;
		} catch (error) {
			console.error('authService.refresh: Token refresh failed:', error);
			throw error;
		}
	}

	/**
	 * Logout current user
	 */
	async logout(): Promise<ApiResponse<void>> {
		try {
			console.log('authService.logout: Attempting logout');

			const response = await apiClient.post<void>(AUTH_ENDPOINTS.LOGOUT);

			console.log('authService.logout: Logout successful');
			return response;
		} catch (error) {
			console.warn(
				'authService.logout: Logout endpoint failed, but continuing with local cleanup:',
				error,
			);
			// Return a mock successful response for local cleanup
			return {
				data: undefined,
				success: true,
				message: 'Logged out locally',
			};
		}
	}

	/**
	 * Get current user profile
	 */
	async getProfile(): Promise<ApiResponse<UserProfile>> {
		try {
			console.log('authService.getProfile: Fetching user profile');

			const response = await apiClient.get<UserProfile>(AUTH_ENDPOINTS.PROFILE);

			console.log('authService.getProfile: Profile fetch successful');
			return response;
		} catch (error) {
			console.error('authService.getProfile: Profile fetch failed:', error);
			throw error;
		}
	}

	/**
	 * Validate if token is still valid
	 */
	async validateToken(token: string): Promise<boolean> {
		try {
			if (!token || token.length === 0) {
				return false;
			}

			// Basic token format validation
			if (!token.includes('.') && token.length < 10) {
				return false;
			}

			// In production, this would make an API call to validate the token
			// For now, just check basic format
			console.log('authService.validateToken: Token validation passed');
			return true;
		} catch (error) {
			console.error('authService.validateToken: Token validation failed:', error);
			return false;
		}
	}
}

// Export singleton instance
export const authService = new AuthService();

// Helper function to extract user info from JWT token
export const getUserFromToken = (token: string): UserProfile | null => {
	try {
		if (!token) return null;

		// In production, this would properly decode the JWT token
		// For now, return null to indicate we need to fetch from API
		console.log('getUserFromToken: JWT decoding not implemented, returning null');
		return null;
	} catch (error) {
		console.error('getUserFromToken: Token parsing failed:', error);
		return null;
	}
};

// Token validation utilities
export const authHelpers = {
	/**
	 * Check if token appears to be expired based on basic checks
	 */
	isTokenExpired: (token: string): boolean => {
		try {
			// Basic checks for token validity
			if (!token || token.length < 10) return true;

			// In production, decode JWT and check exp claim
			// For now, assume token is valid if it exists
			return false;
		} catch {
			return true;
		}
	},

	/**
	 * Extract basic info from token without full JWT decoding
	 */
	getTokenInfo: (token: string): { valid: boolean; length: number } => {
		return {
			valid: !!token && token.length > 0,
			length: token?.length || 0,
		};
	},
};
